import type { BookingPublic } from '@otryad/shared';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { useHold } from '@/components/doc/HoldTimer';
import { Button, Card } from '@/components/ui';
import { useAppFeatures } from '@/lib/features';
import { formatMoney, formatTime } from '@/lib/format';
import { fmt, PLURAL_GATHERINGS, plural, texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

const t = texts.my;

/**
 * Бронь, которая ждёт оплаты, — на экране, с которого приложение открывается (раунд персон 3, M4):
 * плашка с отсчётом жила только в «Моих сборах», а окно в 15 минут утекало незаметно. Истёк срок —
 * плашка уходит сама (N1): список перечитывается, сервер снимает просроченную бронь при чтении.
 */
function Plate({ booking, onExpired }: { booking: BookingPublic; onExpired: () => void }) {
  const navigate = useNavigate();
  const hold = useHold(booking.holdUntil);
  const tz = booking.items[0]?.timezone;
  const live = booking.items.filter((i) => i.unavailableReason === null);
  const over = hold?.over ?? false;
  useEffect(() => {
    if (over) onExpired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over]);
  if (over) return null;
  return (
    <Card accent>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-danger text-base font-semibold">{t.holdTitle}</div>
          <div className="mt-0.5 text-sm font-semibold">
            {fmt(t.holdSummary, {
              count: plural(live.length, PLURAL_GATHERINGS),
              amount: formatMoney(booking.amountKopecks),
            })}
          </div>
          {booking.holdUntil && tz ? (
            <p className="text-muted mt-0.5 text-sm">
              {fmt(t.holdBody, { time: formatTime(booking.holdUntil, tz) })}
            </p>
          ) : null}
        </div>
        {hold ? (
          <span
            className={hold.hot ? 'text-danger num shrink-0 text-2xl font-bold' : 'num shrink-0 text-2xl font-bold'}
            aria-live={hold.hot ? 'polite' : 'off'}
          >
            {hold.label}
          </span>
        ) : null}
      </div>
      <Button fullWidth className="mt-3" onClick={() => void navigate(`/booking/${booking.id ?? ''}`)}>
        {t.holdOpen}
      </Button>
    </Card>
  );
}

/** Плашки всех живых броней человека; без «Моих сборов» и без броней ничего не рисует. */
export function HoldNotice() {
  const { myGatherings } = useAppFeatures();
  const mine = trpc.my.gatherings.useQuery(undefined, {
    enabled: myGatherings,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (q.state.data?.bookings.length ? 15_000 : false),
  });
  const bookings = mine.data?.bookings ?? [];
  if (bookings.length === 0) return null;
  return (
    <>
      {bookings.map((b) =>
        b.id ? <Plate key={b.id} booking={b} onExpired={() => void mine.refetch()} /> : null,
      )}
    </>
  );
}
